import type { Application } from '@hotwired/stimulus';
import { Controller } from '@hotwired/stimulus';

import type { LibAsync, TurboSubmitEndEvent } from './async-library';

/**
 * Adds async functionality like SPA like page navigation for
 * SPA or async form submits. Add `data-controller="async"` to enable.
 * Uses Turbo under the hood.
 * @see https://turbo.hotwired.dev/ for usage
 * @example <body data-controller="async">
 */
export class AsyncController extends Controller<HTMLElement> {
    libAsync: LibAsync;

    static values = {
        navigation: {
            type: Boolean,
            default: false,
        },
    };

    /**
     * Whether SPA navigation should be used or not
     */
    declare readonly navigationValue: Boolean;

    static register(application: Application) {
        application.register('async', this);
    }

    async connect() {
        const initalized = await this.loader();

        if (initalized) {
            this.updateHTMLAnchorElements();
            this.initEventHandlers();

            console.log(`Connect async controller on ${this.element}`);
        }
    }

    async initEventHandlers() {
        this.element.addEventListener('turbo:submit-end', this.submitEnd);
    }

    /**
     * Automatically sets sensible defaults on all HTMLAnchorElements that don't
     * have a preconfigured [target] attribute. Prevents accidental async issues.
     */
    private updateHTMLAnchorElements() {
        this.element.querySelectorAll('a').forEach((a) => {
            if ((!a.target || a?.target === '') && !a.getAttribute('data-turbo-frame')) {
                // Always open links as hard page reloads and not asynchronously if not
                // defined differently
                a.setAttribute('data-turbo-frame', '_top');
            }
        });
    }

    /**
     * Lazy load async library, only when requested by the page
     * in any way, e.g. by having a <turbo-frame> element or
     * by activating SPA navigation via data-async-navigation-value="true"
     */
    private async loader() {
        const turboFrame = document.querySelector('turbo-frame');

        if (turboFrame || this?.navigationValue) {
            const { LibAsync } = await import('./async-library');
            this.libAsync = new LibAsync();

            return true;
        }

        return false;
    }

    /**
     * Handles redirects.
     * Fires after the form submission-initiated network request completes. Access the FormSubmission object with
     * event.detail.formSubmission along with FormSubmissionResult properties included within event.detail.
     */
    private submitEnd(event: TurboSubmitEndEvent) {
        const { fetchResponse, error } = event.detail;

        if (error) {
            console.error('Async Controller Error:', error);
            return;
        }

        const { status, url, redirected } = fetchResponse?.response;

        if (status !== 200) {
            if (redirected) {
                // Redirect needs to be handled server-side. We just refresh the page
                // so the server can appropriately handle the redirect.
                window.location.href = url;
            }
        }
    }
}
